import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {concat, finalize, last, Observable, of, switchMap} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {BusinessDirectoryService} from '../api/business-directory.service';
import {BusinessDirectory, BizLookingFor} from '../models/business-directory';
import {BUSINESS_CATEGORIES_GROUPED, STATES} from '../constants';
import {BIZ_SIZES, LOOKING_FOR, SERVICE_AREAS} from '../biz-directory/biz-categories';
import {SpinnerService} from '../shared/services/spinner.service';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_PHOTOS = 5;
const WEB_ADDRESS = '^(https?://)?([\\da-z.-]+)\\.([a-z.]{2,24})([/?#].*)?$';

/** Add or edit a listing (/biz-directory/new, /biz-directory/:id/edit). Members only; the API enforces it. */
@Component({
    selector: 'app-biz-form',
    templateUrl: './biz-form.component.html',
    styleUrls: ['./biz-form.component.css']
})
export class BizFormComponent implements OnInit {
    form!: FormGroup;
    categories = BUSINESS_CATEGORIES_GROUPED;
    states = STATES;
    serviceAreas = SERVICE_AREAS;
    sizes = BIZ_SIZES;
    lookingForOptions = LOOKING_FOR;
    thisYear = new Date().getFullYear();

    editingId: string | null = null;
    allowed = true;
    offeringsText = '';          // comma separated; split on save (the app's usual idiom for lists)
    selectedLooking = new Set<BizLookingFor>();

    existingLogo?: string;
    existingPhotos: string[] = [];
    logoFile: File | null = null;
    logoPreview: string | null = null;
    photoFiles: File[] = [];
    photoPreviews: string[] = [];

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private bizService: BusinessDirectoryService,
        private spinner: SpinnerService,
        private toastr: ToastrService
    ) {
    }

    ngOnInit(): void {
        const perms = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
        this.allowed = !!perms?.BUSINESS?.includes('CREATE');
        this.form = this.fb.group({
            businessName: ['', [Validators.required, Validators.maxLength(120)]],
            industry: ['', Validators.required],
            tagline: ['', Validators.maxLength(140)],
            description: [''],
            website: ['', Validators.pattern(WEB_ADDRESS)],
            city: ['', Validators.maxLength(80)],
            state: [''],
            serviceArea: [''],
            size: [''],
            yearStarted: [null, [Validators.min(1900), Validators.max(this.thisYear)]],
            phone: ['', Validators.pattern('^[0-9+()\\-. xX]{7,25}$')],
            email: ['', Validators.email],
            whatsapp: ['', Validators.pattern('^[0-9+()\\-. xX]{7,25}$')],
            linkedin: ['', Validators.pattern(WEB_ADDRESS)],
            instagram: ['', Validators.pattern(WEB_ADDRESS)],
            facebook: ['', Validators.pattern(WEB_ADDRESS)],
            memberOffer: ['', Validators.maxLength(200)],
            mentorAvailable: [false]
        });

        const id = this.route.snapshot.paramMap.get('id');
        if (id && this.allowed) {
            this.editingId = id;
            this.spinner.show();
            this.bizService.getBusinessById(id).pipe(finalize(() => this.spinner.hide())).subscribe({
                next: biz => {
                    if (!biz.canEdit) {
                        this.toastr.error('Only the owner can edit this listing');
                        this.router.navigate(['/biz-directory', id]);
                        return;
                    }
                    this.form.patchValue({...biz, yearStarted: biz.yearStarted ?? null});
                    this.offeringsText = (biz.offerings || []).join(', ');
                    this.selectedLooking = new Set(biz.lookingFor || []);
                    this.existingLogo = biz.logoUrl;
                    this.existingPhotos = biz.photos || [];
                },
                error: err => {
                    console.error('Failed to load business for editing', err);
                    this.toastr.error('Could not load the listing');
                    this.router.navigate(['/biz-directory']);
                }
            });
        }
    }

    get photoSlotsLeft(): number {
        return MAX_PHOTOS - this.existingPhotos.length - this.photoFiles.length;
    }

    toggleLooking(value: BizLookingFor): void {
        if (this.selectedLooking.has(value)) this.selectedLooking.delete(value);
        else this.selectedLooking.add(value);
    }

    onLogoPicked(event: Event): void {
        const file = this.pick(event);
        if (!file) return;
        this.logoFile = file;
        this.preview(file, url => this.logoPreview = url);
    }

    onPhotosPicked(event: Event): void {
        const input = event.target as HTMLInputElement;
        for (const file of Array.from(input.files || [])) {
            if (this.photoSlotsLeft <= 0) {
                this.toastr.warning(`A listing can have at most ${MAX_PHOTOS} photos`);
                break;
            }
            if (!this.valid(file)) continue;
            this.photoFiles.push(file);
            this.preview(file, url => this.photoPreviews.push(url));
        }
        input.value = '';
    }

    removePendingPhoto(i: number): void {
        this.photoFiles.splice(i, 1);
        this.photoPreviews.splice(i, 1);
    }

    removeExistingPhoto(url: string): void {
        if (!this.editingId) return;
        this.bizService.removePhoto(this.editingId, url).subscribe({
            next: biz => this.existingPhotos = biz.photos || [],
            error: err => {
                console.error('Failed to remove photo', err);
                this.toastr.error('Could not remove the photo');
            }
        });
    }

    save(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            this.toastr.error('Please fix the highlighted fields');
            return;
        }
        const body: BusinessDirectory = {
            ...this.form.value,
            offerings: this.offeringsText.split(',').map(t => t.trim()).filter(t => t),
            lookingFor: Array.from(this.selectedLooking),
            yearStarted: this.form.value.yearStarted || null
        };
        this.spinner.show();
        const save$ = this.editingId ? this.bizService.updateBusiness(this.editingId, body) : this.bizService.createBusiness(body);
        const creating = !this.editingId;
        let listingSaved = false;
        save$.pipe(
            // images go up after the listing exists, since an upload is addressed by business id; remember the id so
            // a failed upload followed by another click edits this listing instead of creating a second one
            switchMap(biz => {
                listingSaved = true;
                this.editingId = biz.id!;
                return this.uploadImages(biz.id!).pipe(switchMap(() => of(biz)));
            }),
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: biz => {
                this.toastr.success(creating ? 'Business added' : 'Listing updated');
                this.router.navigate(['/biz-directory', biz.id]);
            },
            error: err => {
                console.error('Failed to save business', err);
                const reason = err?.error?.message || 'Could not save the listing';
                this.toastr.error(listingSaved ? `Your listing was saved, but an image failed: ${reason}` : reason);
            }
        });
    }

    private uploadImages(id: string): Observable<unknown> {
        const uploads: Observable<unknown>[] = [];
        if (this.logoFile) uploads.push(this.bizService.uploadLogo(id, this.logoFile));
        this.photoFiles.forEach(f => uploads.push(this.bizService.uploadPhoto(id, f)));
        // one at a time: each upload re-reads the listing, so parallel uploads could overwrite each other
        return uploads.length ? concat(...uploads).pipe(last()) : of(null);
    }

    private pick(event: Event): File | null {
        const file = (event.target as HTMLInputElement).files?.[0];
        return file && this.valid(file) ? file : null;
    }

    private valid(file: File): boolean {
        if (!/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) {
            this.toastr.error('Only jpg, png, gif or webp images are allowed');
            return false;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            this.toastr.error('Images must be 5 MB or smaller');
            return false;
        }
        return true;
    }

    private preview(file: File, done: (url: string) => void): void {
        const reader = new FileReader();
        reader.onload = () => done(reader.result as string);
        reader.readAsDataURL(file);
    }
}
